import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useMapEditorStore } from './mapEditorStore'

/**
 * DOM/CSS grid map editor. Pointer paint with drag support for desktop + touch.
 */
export const MapEditorGrid: React.FC = () => {
  const map = useMapEditorStore((s) => s.map)
  const paint = useMapEditorStore((s) => s.paint)
  const beginPaintStroke = useMapEditorStore((s) => s.beginPaintStroke)
  const endPaintStroke = useMapEditorStore((s) => s.endPaintStroke)
  const setHover = useMapEditorStore((s) => s.setHover)
  const painting = useRef(false)
  const paintedByPointer = useRef(false)

  const pathKeys = useMemo(() => new Set(map.path.map((c) => `${c.x},${c.y}`)), [map.path])

  const cellFromEvent = useCallback(
    (target: EventTarget | null): { x: number; y: number } | null => {
      if (!(target instanceof HTMLElement)) return null
      const cell = target.closest('[data-cell]') as HTMLElement | null
      if (!cell) return null
      const x = Number(cell.dataset.x)
      const y = Number(cell.dataset.y)
      if (!Number.isInteger(x) || !Number.isInteger(y)) return null
      return { x, y }
    },
    []
  )

  useEffect(() => {
    const end = () => {
      if (!painting.current) return
      painting.current = false
      endPaintStroke()
    }
    window.addEventListener('pointerup', end)
    window.addEventListener('pointercancel', end)
    return () => {
      window.removeEventListener('pointerup', end)
      window.removeEventListener('pointercancel', end)
    }
  }, [endPaintStroke])

  const cellSize = map.width * map.height > 1600 ? 12 : undefined

  return (
    <div className="map-editor__grid-scroll" data-testid="map-editor-grid">
      <div
        className="map-editor__grid"
        style={{
          gridTemplateColumns: `repeat(${map.width}, minmax(${cellSize ?? 14}px, 1fr))`,
          gridTemplateRows: `repeat(${map.height}, minmax(${cellSize ?? 14}px, 1fr))`
        }}
        onPointerLeave={() => setHover(null)}
      >
        {map.rows.map((row, y) =>
          [...row].map((tile, x) => {
            const isSpawn = map.spawn.x === x && map.spawn.y === y
            const isExit = map.exit.x === x && map.exit.y === y
            const orphanPath = tile === 'P' && pathKeys.size > 0 && !pathKeys.has(`${x},${y}`)
            const classes = [
              'map-editor__cell',
              tile === 'P' ? 'map-editor__cell--path' : 'map-editor__cell--grass',
              isSpawn ? 'map-editor__cell--spawn' : '',
              isExit ? 'map-editor__cell--exit' : '',
              orphanPath ? 'map-editor__cell--orphan' : ''
            ]
              .filter(Boolean)
              .join(' ')

            return (
              <button
                key={`${x},${y}`}
                type="button"
                data-cell
                data-x={x}
                data-y={y}
                data-testid={`cell-${x}-${y}`}
                className={classes}
                style={cellSize ? { width: cellSize, height: cellSize } : undefined}
                aria-label={`Cell ${x},${y} ${tile}${isSpawn ? ' spawn' : ''}${isExit ? ' exit' : ''}`}
                onPointerDown={(event) => {
                  event.preventDefault()
                  ;(event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId)
                  painting.current = true
                  paintedByPointer.current = true
                  beginPaintStroke()
                  paint(x, y)
                }}
                onClick={(event) => {
                  if (paintedByPointer.current) {
                    paintedByPointer.current = false
                    return
                  }
                  event.preventDefault()
                  beginPaintStroke()
                  paint(x, y)
                  endPaintStroke()
                }}
                onPointerEnter={(event) => {
                  setHover({ x, y })
                  if (!painting.current) return
                  if (event.buttons === 0 && event.pointerType === 'mouse') return
                  paint(x, y)
                }}
                onPointerMove={(event) => {
                  if (!painting.current) return
                  const next = cellFromEvent(document.elementFromPoint(event.clientX, event.clientY))
                  if (next) paint(next.x, next.y)
                }}
              >
                {isSpawn ? 'S' : isExit ? 'E' : ''}
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}
